import React, { FunctionComponent, useEffect } from "react";
import styles from "./TextImage.module.scss";
import TextImageProps from "./TextImageProps.interface";

import { motion, useAnimation } from "framer-motion";
import { useDispatch } from "react-redux";
import { useInView } from "react-intersection-observer";
import Anchor from "components/Anchor/Anchor";
import BubbleBackground from "components/BubbleBackground/BubbleBackground";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import getImageAspectRatio from "app/media/helpers/getImageAspectRatio";
import getImageCrop from "app/media/helpers/getImageCrop";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import handleOpenModal from "blocks/helpers/handleOpenModal";
import ImageViewer from "components/ImageViewer/ImageViewer";
import Paragraph from "components/Paragraph/Paragraph";
import useAnchorIdentifier from "hooks/useAnchorIdentifier";
import Button from "components/Button/Button";
import isBrowserContext from "helpers/isBrowserContext";

const TextImage: FunctionComponent<TextImageProps> = (props) => {
    const wrapperClasses = getWrapperClasses(props.settings);
    const identifier = useAnchorIdentifier(props.settings.anchorIdentifier);

    const cropSetting = props.content.imageMask && props.content.imageMask.length === 0 ? props.settings.imageCrop : undefined;
    const imageCrop = getImageCrop(props.content.image?.umbracoFile.crops, cropSetting, "Square (1:1)");
    const imageAspectRatio = getImageAspectRatio(imageCrop, props.content.image?.umbracoWidth, props.content.image?.umbracoHeight);
    const imagePositionClass = props.settings.imagePosition === "Media Right" ? ` ${styles.has_image_right}` : undefined;

    const hasMask = props.content.imageMask && props.content.imageMask.length > 0;
    const masked = props.content.imageMask && props.content.imageMask.length > 0 ? props.content.imageMask[0].content._contentType : undefined;
    const detailColor = props.content.imageMask && props.content.imageMask.length > 0 ? props.content.imageMask[0].content.circleColor : undefined;

    const dispatch = useDispatch();

    // ANIMATION
    const controls = useAnimation();
    const { ref, inView } = useInView({ threshold: 0.3 });

    const animateUp = {
        visible: { y: 0, opacity: 1 },
        hidden: { y: 80, opacity: 0 }
    };
    const animateDown = {
        visible: { y: 0, opacity: 1 },
        hidden: { y: -80, opacity: 0 }
    };

    const selectAnimation = props.settings.animation && props.settings.animation.length > 0 ? props.settings.animation[0].content._contentType : undefined;
    const selectVariants = selectAnimation === "animateUp" ? animateUp : selectAnimation === "animateDown" ? animateDown : undefined;
    const repeatAnimation = props.settings.animation && props.settings.animation.length > 0 ? props.settings.animation[0].content.repeatAnimation : false;

    useEffect(() => {
        if (inView) {
            controls.start("visible");
        }
        if (!inView && repeatAnimation) {
            controls.start("hidden");
        }
    }, [controls, inView]);

    return (
        <motion.section className={combineClasses([styles.wrapper, ...wrapperClasses])} id={identifier} ref={ref} initial={isBrowserContext() ? "hidden" : "shown"} animate={controls} variants={selectVariants} transition={{ duration: 0.4 }}>
            <div className={combineClasses([styles.container, imagePositionClass])}>
                <div className={styles.media_wrapper}>
                    <div className={combineClasses([styles.media, styles[masked]])}>
                        {/* Image */}
                        {props.content.image ? (
                            <ImageViewer src={props.content.image.umbracoFile.src} crop={imageCrop} focalPoint={props.content.image.umbracoFile.focalPoint} alt={props.content.imageAlternative} aspectRatioX={imageAspectRatio.aspectRatioX} aspectRatioY={imageAspectRatio.aspectRatioY} />
                        ) : null}

                        {/* Caption */}
                        {props.content.imageCaption && props.content.imageCaption.length > 0 && props.content.imageMask?.length === 0 ? (
                            <div className={styles.caption}>{props.content.imageCaption}</div>
                        ) : null}
                    </div>

                    {hasMask ? (
                        <div className={styles.media_detail} style={{ backgroundColor: detailColor }} />
                    ) : null}
                </div>

                <div className={combineClasses([styles.content])}>
                    {/* Tagline */}
                    {props.content.tagline ? (
                        <span className={styles.tagline}>{props.content.tagline}</span>
                    ) : null}

                    {/* Headline */}
                    {props.content.headline ? (
                        <DynamicHeading className={styles.headline} headlineSize={props.settings.headlineSize} headlineWeight={props.settings.headlineWeight}>{props.content.headline}</DynamicHeading>
                    ) : null}

                    {/* Text */}
                    {props.content.bodyText ? (
                        <Paragraph className={styles.text} innerHTML={props.content.bodyText} />
                    ) : null}

                    {/* Buttons */}
                    {props.content.buttons && props.content.buttons.length > 0 ? (
                        <div className={styles.buttons}>
                            {props.content.buttons.map((button) => {
                                return (
                                    <Button key={button.contentUdi} className={styles.button} modal={button.content.modal} onClick={() => { button.content.modal ? handleOpenModal(button.content.modal, button.content.stickyLink, dispatch) : undefined; }} to={button.content.link} parameters={button.content.parameters} anchor={button.content.anchor} settings={button.settings}>
                                        {button.content.label}
                                    </Button>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
            </div>

            {props.settings.bubbleBackground && props.settings.bubbleBackground.length > 0 ? (
                <BubbleBackground {...props.settings.bubbleBackground[0]} />
            ) : null}
        </motion.section>
    );
};

export default TextImage;